import classnames from 'classnames';
import React, { useState } from 'react';

import { formatAccountNumber, formatCardNumber } from '@eika-infrastruktur/format';
import { Checkbox } from '@eika/checkbox';
import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel } from '@eika/label';

import { GroupedSortableDnd } from '..';
import { AccountLabels } from '../../../../../../components/shared/AccountLabels';
import { useConfig } from '../../../../../../defaultConfig';
import useTranslation from '../../../../../../hooks/useTranslation';
import { AccountCardItem } from '../../../../../../models/account';

import './EditAccountBox.scss';

interface Props<T extends AccountCardItem> {
    account: T;
    checked: boolean;
    onChange: (account: T, checked: boolean) => void;
    moveUp: (account: T) => boolean;
    moveDown: (account: T) => boolean;
    focused: boolean;
    invalid: boolean;
}

function EditAccountBox<T extends AccountCardItem>({
    account,
    checked,
    onChange,
    moveUp,
    moveDown,
    focused,
    invalid,
    ...rest
}: Props<T>) {
    const { featureToggles } = useConfig();
    const [shake, setShake] = useState(false);
    const [shakeDown, setShakeDown] = useState(false);

    const translated = useTranslation();

    const liftUp = () => {
        const shouldShake = moveUp(account);
        if (shouldShake) {
            setShake(true);
            setShakeDown(false);
        }
    };

    const pullDown = () => {
        const shouldShake = moveDown(account);
        if (shouldShake) {
            setShake(true);
            setShakeDown(true);
        }
    };

    const handleChange = (checked: boolean) => {
        onChange(account, checked);
    };

    return (
        <>
            <div
                className={classnames('edit-account-box', {
                    'edit-account-box--shake': shake,
                    'edit-account-box--shake-down': shakeDown,
                    'edit-account-box--focused': focused,
                    'edit-account-box--checked': checked,
                    'edit-account-box--invalid': invalid,
                })}
                onAnimationEnd={() => setShake(false)}
                {...rest}
            >
                <div>
                    <button
                        type="button"
                        className="edit-account__btn edit-account__btn-up"
                        onClick={liftUp}
                        aria-label={translated.editAccounts.moveUp}
                    >
                        <Icon type={IconTypes.LITEN_PIL_OPP} />
                    </button>
                    <button
                        type="button"
                        className="edit-account__btn edit-account__btn-down"
                        onClick={pullDown}
                        aria-label={translated.editAccounts.moveDown}
                    >
                        <Icon type={IconTypes.LITEN_PIL_NED} />
                    </button>
                </div>
                {featureToggles.enableAccountsDnd && (
                    <>
                        <GroupedSortableDnd.DragHandleDots />
                        <div className="edit-account-box__content">
                            <div className="edit-account-box__account-owner">
                                {account.account && <AccountLabels owners={account.account.owners} />}
                            </div>

                            <label
                                className="edit-account-box__label"
                                htmlFor={`edit-account-checkbox-${account.accountNumber.value}`}
                            >
                                {account.alias}
                            </label>
                            {account.account ? (
                                <p className="edit-account-box__account-number">
                                    {formatAccountNumber(account.accountNumber)}
                                </p>
                            ) : (
                                <p className="edit-account-box__account-number">
                                    {formatCardNumber(account.card!.number.value)}
                                </p>
                            )}
                        </div>
                    </>
                )}
                {!featureToggles.enableAccountsDnd && (
                    <div className="edit-account-box__content">
                        <div className="edit-account-box__account-owner">
                            {account.account && <AccountLabels owners={account.account.owners} />}
                        </div>

                        <label className="edit-account-box__label">{account.alias}</label>
                        {account.account ? (
                            <p className="edit-account-box__account-number">
                                {formatAccountNumber(account.accountNumber)}
                            </p>
                        ) : (
                            <p className="edit-account-box__account-number">
                                {formatCardNumber(account.card!.number.value)}
                            </p>
                        )}
                    </div>
                )}
                <Checkbox
                    className="edit-account-box__check-box"
                    id={`edit-account-checkbox-${account.accountNumber.value}`}
                    onValueChange={handleChange}
                    checked={checked}
                />
            </div>
            {invalid && <ErrorLabel icon={IconTypes.INFO_24} text={translated.editAccounts.cantDisable} />}
        </>
    );
}

export default EditAccountBox;
